import { Autocomplete, Chip, TextField } from '@mui/material';
import { FC, useCallback, useState } from 'react';

import { isValidEmail } from '../../utils';

type Props = {
	emailAddresses: string[];
	setEmailAddresses: (emails: string[]) => void;
	label?: string;
	hasError?: boolean;
	helperText?: string;
};

export const EmailsInput: FC<Props> = ({
	emailAddresses,
	setEmailAddresses,
	label = 'Emails',
	hasError,
	helperText,
}) => {
	const [inputValue, setInputValue] = useState('');
	const [error, setError] = useState<string | null>(null);

	const validateEmail = useCallback(() => {
		const email = inputValue.trim();
		if (!email) {
			return null;
		}

		if (!isValidEmail(email)) {
			setError('Invalid email address');
			return null;
		}

		if (emailAddresses.includes(email)) {
			setError('Email already added');
			return null;
		}

		return email;
	}, [inputValue, setError, emailAddresses]);

	const addEmail = useCallback(() => {
		const email = validateEmail();
		if (!email) {
			return;
		}

		setEmailAddresses([...emailAddresses, email]);
		setInputValue('');
		setError(null);
	}, [setError, setEmailAddresses, setInputValue, emailAddresses, validateEmail]);

	const onChange = (addresses: string[]) => {
		const validAddresses = addresses.filter((address) => isValidEmail(address));
		setEmailAddresses(validAddresses);
	};

	return (
		<Autocomplete
			multiple
			freeSolo
			options={[]}
			value={emailAddresses}
			inputValue={inputValue}
			onInputChange={(_, value) => {
				if (value) {
					setError(null);
				}
				setInputValue(value);
			}}
			onChange={(_, addresses) => onChange(addresses)}
			onKeyDown={(e) => {
				if (['Enter', ','].includes(e.key)) {
					e.preventDefault();
					addEmail();
				}
			}}
			onBlur={() => addEmail()}
			renderTags={(emails, getTagProps) =>
				emails.map((email, index) => <Chip {...getTagProps({ index })} key={email} label={email} />)
			}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					error={hasError || error !== null}
					helperText={error || helperText}
					placeholder={emailAddresses.length === 0 ? 'Enter email addresses' : null}
				/>
			)}
		/>
	);
};
